<template>
    <div class="msku-manage-page">
        <!-- 顶部操作栏 -->
        <div class="operation-bar">
            <div class="search-form">
                <span class="form-label">店铺</span>
                <el-select v-model="searchForm.shop" placeholder="选择店铺" style="width:180px" filterable
                    @change="onShopChange" size="small">
                    <el-option v-for="item in filters" :key="item.value" :label="item.text" :value="item.value" />
                </el-select>
                <span class="form-label">本地父体</span>
                <el-select v-model="searchForm.parentName" placeholder="选择父体" style="width:260px" filterable clearable
                    size="small">
                    <el-option v-for="p in parentOptions" :key="p.value" :label="p.text" :value="p.value" />
                </el-select>
                <el-button type="primary" size="small" @click="handleSearch" :loading="isSending">查询</el-button>
                <el-button size="small" @click="resetSearch">重置</el-button>
                <!-- 首期上线保留：生成 MSKU 前的父体级基础配置。 -->
                <el-button size="small" type="warning" plain @click="openParentConfig">父体配置</el-button>
                <!-- 首期上线保留：查看 MSKU 轮询、FNSKU/ASIN 与配对同步状态。 -->
                <el-button size="small" type="warning" plain @click="openAutoWorkbench('msku')">Auto工作台</el-button>
                <!-- 当前阶段开放：品名/父体配置模板下载、批量导入、颜色翻译维护。 -->
                <el-button size="small" type="success" plain @click="openBatchDialog">批量导入配置</el-button>
                <!-- 品名模板用于移入/移出流程，当前 MSKU 首期暂不开放批量生成品名模板。 -->
                <!--
                <el-button size="small" type="success" plain @click="openFillTplDialog('pm')"
                    :disabled="!tableSelection.length">生成品名模板{{ tableSelection.length ? '(' + tableSelection.length +
                        ')' : '' }}</el-button>
                -->
                <el-button size="small" type="success" plain @click="openFillTplDialog('parent')">父体模板</el-button>
                <!-- 后续阶段再开放：批量更新在售MSKU；批量生成中的单行配置仍可编辑 MSKU 颜色。 -->
                <!--
                <el-button size="small" type="success" plain @click="openMskuBatch">批量更新在售MSKU</el-button>
                -->
                <el-button size="small" type="info" plain @click="openColorConfig">颜色配置</el-button>
                <!-- 首期上线保留：批量生成 MSKU，并写入 Auto 监控队列。 -->
                <el-button size="small" type="warning" plain @click="openGenMulti">批量生成MSKU{{ tableSelection.length ? '(' + tableSelection.length + ')' : '' }}</el-button>
            </div>
        </div>

        <!-- 数据表格区域 -->
        <div class="table-area">
            <div class="table-header-bar">
                <span class="table-title">品名列表</span>
                <el-input v-model="pmFilter" placeholder="筛选本地品名" size="small" style="width:200px" clearable
                    prefix-icon="el-icon-search" />
            </div>
            <el-table ref="mainTable" :data="filteredTableData" style="width:100%;margin-top:20px" border stripe
                :row-class-name="tableRowClass" :row-key="tableRowKey" v-loading="isSending"
                @selection-change="onTableSelectionChange">
                <el-table-column type="selection" width="45" :reserve-selection="true" />
                <el-table-column prop="本地父体" label="本地父体" min-width="140" sortable />
                <el-table-column prop="本地品名" label="本地品名" min-width="140" />
                <el-table-column prop="店铺" label="店铺" width="120" />
                <el-table-column prop="msku" label="当前售卖MSKU" min-width="220">
                    <template slot-scope="scope">
                        <div v-if="scope.row._mskuEditing" style="display:flex;align-items:center;">
                            <el-input v-model="scope.row.msku_edit" size="mini" style="flex:1;min-width:120px;"
                                placeholder="填写要绑定的MSKU" @keyup.enter.native="saveMskuEdit(scope.row)" />
                            <el-button type="primary" size="mini" icon="el-icon-check" style="margin-left:4px;"
                                :loading="scope.row._mskuSaving" @click="saveMskuEdit(scope.row)" />
                            <el-button size="mini" icon="el-icon-close" style="margin-left:2px;"
                                @click="cancelMskuEdit(scope.row)" />
                        </div>
                        <div v-else class="msku-cell" title="点击编辑MSKU" @click="startMskuEdit(scope.row)">
                            <span v-if="!scope.row._hasRel" style="color:#C0C4CC;">/</span>
                            <span v-else-if="!scope.row.msku" style="color:#E6A23C;">未绑定</span>
                            <span v-else>{{ scope.row.msku }}</span>
                            <i class="el-icon-edit-outline msku-edit-icon"></i>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column prop="状态" label="状态" width="130" align="center">
                    <template slot-scope="scope">
                        <el-tag :type="statusType(scope.row.状态)" size="small" effect="plain">
                            {{ scope.row['状态'] || '待操作' }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="品名配置" width="100" align="center">
                    <template slot-scope="scope">
                        <el-tag size="small" :type="scope.row['品名配置'] === '已配置' ? 'success' : 'info'">
                            {{ scope.row['品名配置'] || '未配置' }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="110" align="center" fixed="right">
                    <template #default="{ row }">
                        <el-dropdown trigger="click" @command="cmd => onRowCommand(cmd, row)">
                            <el-button size="mini" type="primary" plain>
                                编辑<i class="el-icon-arrow-down el-icon--right"></i>
                            </el-button>
                            <el-dropdown-menu slot="dropdown">
                                <!-- 首期上线保留：生成 MSKU 所需的品名级配置。 -->
                                <el-dropdown-item command="config">配置</el-dropdown-item>
                                <!--
                                首期暂不上线：该入口走旧的单条复合流程，不写入 Auto MSKU 监控队列。
                                <el-dropdown-item command="create_pair">创建产品并配对</el-dropdown-item>
                                <el-dropdown-item command="move_in">移入</el-dropdown-item>
                                <el-dropdown-item command="move_out">移出</el-dropdown-item>
                                <el-dropdown-item command="reset" divided>重置</el-dropdown-item>
                                -->
                            </el-dropdown-menu>
                        </el-dropdown>
                    </template>
                </el-table-column>
            </el-table>
        </div>

        <!-- 移入弹窗 -->
        <el-dialog title="移入操作" :visible.sync="moveInVisible" width="580px" custom-class="au-dialog"
            :close-on-click-modal="false" append-to-body>
            <div v-if="moveInStep === 'form'">
                <el-form label-width="140px" size="small">
                    <el-form-item label="源MSKU" required>
                        <el-input v-model="moveInRow.msku" placeholder="要跟卖的源产品 MSKU" style="width:100%" />
                        <div class="msku-hint">取该 MSKU 的 ASIN 与属性，创建新跟卖产品并移入目标父体</div>
                    </el-form-item>
                    <el-form-item label="新MSKU" required>
                        <el-input v-model="moveInRow.new_msku" placeholder="按品名配置自动生成(-C)，可修改" style="width:100%" />
                        <div class="msku-hint">按父体「MSKU 生成模式」+ 品名配置自动生成（后缀 -C），创建成功后录入为当前售卖MSKU</div>
                    </el-form-item>
                    <el-form-item label="Parent SKU" required>
                        <el-input v-model="moveInRow.parent_sku" placeholder="要移入的父体 MSKU" style="width:100%" />
                        <div class="msku-hint">选择要把新 MSKU 移入的父体 MSKU</div>
                    </el-form-item>
                    <el-form-item label="店铺" required>
                        <el-select v-model="moveInRow['店铺']" filterable placeholder="选择店铺" style="width:100%">
                            <el-option v-for="item in filters" :key="item.value" :label="item.text"
                                :value="item.value" />
                        </el-select>
                    </el-form-item>
                    <el-form-item label="产品类型" required>
                        <el-select v-model="moveInRow.product_type" filterable placeholder="选择产品类型" style="width:100%"
                            @change="onMoveInProductTypeChange">
                            <el-option v-for="pt in productTypes" :key="pt.value" :label="pt.label" :value="pt.value" />
                        </el-select>
                        <div class="msku-hint">不同父体的产品类型不同，变体主题随产品类型变化</div>
                    </el-form-item>
                    <el-form-item label="变体主题" required>
                        <el-select v-model="moveInRow.variation_theme" filterable clearable placeholder="选择变体主题"
                            style="width:100%">
                            <el-option v-for="vt in moveInThemes" :key="vt.value" :label="vt.label" :value="vt.value" />
                        </el-select>
                        <div class="msku-hint">按变体主题校验缺失的变体字段（如颜色），补充后重新提交</div>
                    </el-form-item>
                </el-form>
                <div style="text-align:center;margin:10px 0;">
                    <el-button type="info" icon="el-icon-search" @click="moveInPreview" :loading="moveInLoading"
                        :disabled="!moveInRow.msku || !moveInRow.new_msku || !moveInRow.parent_sku || !moveInRow.product_type || !moveInRow.variation_theme">预览检查</el-button>
                </div>
            </div>
            <div v-if="moveInStep === 'preview'">
                <el-alert :title="moveInPreviewData.message"
                    :type="moveInPreviewData.status === 'ready' ? 'success' : 'warning'" :closable="false" show-icon
                    style="margin-bottom:12px;" />
                <el-descriptions v-if="moveInPreviewData.cloned_count" :column="2" size="small" border
                    style="margin-bottom:12px;">
                    <el-descriptions-item label="Product Type">{{ moveInPreviewData.product_type
                    }}</el-descriptions-item>
                    <el-descriptions-item label="源ASIN">{{ moveInPreviewData.source_asin || '-'
                    }}</el-descriptions-item>
                    <el-descriptions-item label="新MSKU">{{ moveInRow.new_msku }}</el-descriptions-item>
                    <el-descriptions-item label="已克隆字段">{{ moveInPreviewData.cloned_count }} 个</el-descriptions-item>
                </el-descriptions>
                <div v-if="moveInPreviewData.missing_fields && moveInPreviewData.missing_fields.length">
                    <p style="color:#E6A23C;font-weight:bold;margin-bottom:8px;">
                        <i class="el-icon-warning"></i> 以下变体字段缺失，请补充：
                    </p>
                    <el-form label-width="120px" size="small">
                        <el-form-item v-for="mf in moveInPreviewData.missing_fields" :key="mf.field_name"
                            :label="mf.title_zh + ' (' + mf.field_name + ')'">
                            <el-select v-if="mf.enum && mf.enum.length" v-model="moveInExtraValues[mf.field_name]"
                                filterable allow-create default-first-option clearable style="width:100%"
                                :placeholder="mf.description || '请输入值'">
                                <el-option v-for="opt in mf.enum" :key="opt" :label="opt" :value="opt" />
                            </el-select>
                            <el-input v-else v-model="moveInExtraValues[mf.field_name]"
                                :placeholder="mf.description || '请输入值'" />
                        </el-form-item>
                    </el-form>
                </div>
                <div v-if="moveInPreviewData.existing_variation_fields && moveInPreviewData.existing_variation_fields.length"
                    style="margin-top:8px;">
                    <p style="color:#67C23A;"><i class="el-icon-check"></i>
                        源产品已有：{{ moveInPreviewData.existing_variation_fields.join('、') }}</p>
                </div>
            </div>
            <div v-if="moveInStep === 'executing'">
                <el-result icon="success" title="移入提交成功" sub-title="后台自动处理中，可关闭弹窗或查看任务面板">
                    <template slot="extra">
                        <el-descriptions :column="1" size="small" border>
                            <el-descriptions-item label="Task ID">{{ moveInResult.task_id }}</el-descriptions-item>
                            <el-descriptions-item label="Record ID">{{ moveInResult.record_unique_id
                            }}</el-descriptions-item>
                        </el-descriptions>
                        <el-button size="small" type="info" @click="moveInVisible = false; openTaskPanel()"
                            style="margin-top:10px;">打开任务面板查看</el-button>
                    </template>
                </el-result>
            </div>
            <span slot="footer">
                <el-button @click="moveInVisible = false; moveInStep = 'form'">关闭</el-button>
                <el-button v-if="moveInStep === 'preview'" @click="backToMoveInForm">上一步</el-button>
                <el-button v-if="moveInStep === 'preview'" type="primary" icon="el-icon-upload2" @click="moveInExecute"
                    :loading="moveInLoading">确认移入</el-button>
            </span>
        </el-dialog>

        <!-- 移出弹窗 -->
        <el-dialog title="移出操作" :visible.sync="moveOutVisible" width="480px" custom-class="au-dialog"
            :close-on-click-modal="false" append-to-body>
            <div v-if="moveOutStep === 'confirm'">
                <el-form label-width="120px" size="small">
                    <el-form-item label="MSKU" required>
                        <el-input v-model="moveOutRow.msku" placeholder="要移出的 MSKU" style="width:100%"
                            @change="onMoveOutMskuChange" />
                        <div class="msku-hint">自动带出当前售卖MSKU；未绑定时需手动输入</div>
                    </el-form-item>
                    <el-form-item label="店铺" required>
                        <el-select v-model="moveOutRow['店铺']" filterable placeholder="选择店铺" style="width:100%">
                            <el-option v-for="item in filters" :key="item.value" :label="item.text"
                                :value="item.value" />
                        </el-select>
                    </el-form-item>
                    <el-form-item label="移出标题">
                        <el-input v-model="moveOutRow.item_name" placeholder="取自品名/父体配置的移出标题，可修改" style="width:100%" />
                        <div class="msku-hint">移出时将覆盖产品标题；留空则保持原标题</div>
                    </el-form-item>
                    <el-form-item label="移出副标题">
                        <el-input v-model="moveOutRow.title_differentiation" placeholder="取自品名/父体配置的移出副标题，可修改"
                            style="width:100%" />
                        <div class="msku-hint">移出时将覆盖产品副标题；留空则保持原副标题</div>
                    </el-form-item>
                </el-form>
                <el-alert title="确认移出操作" type="warning" :closable="false" show-icon style="margin-bottom:12px;" />
                <el-descriptions :column="1" size="small" border>
                    <el-descriptions-item label="本地品名">{{ moveOutRow.本地品名 }}</el-descriptions-item>
                    <el-descriptions-item label="店铺">{{ moveOutRow.店铺 }}</el-descriptions-item>
                </el-descriptions>
                <p v-if="moveOutPreviewInfo.has_parent_relation" style="color:#E6A23C;margin-top:10px;">
                    <i class="el-icon-warning"></i> 该产品存在父子关系，移出后将修改 parent_sku 为自身，使其独立。
                </p>
                <p v-else style="color:#909399;margin-top:10px;">该产品已是独立产品。</p>
            </div>
            <div v-if="moveOutStep === 'executing'">
                <el-result icon="success" title="移出提交成功" sub-title="后台处理中">
                    <template slot="extra">
                        <el-tag size="small">Task ID: {{ moveOutResult.task_id }}</el-tag>
                    </template>
                </el-result>
            </div>
            <span slot="footer">
                <el-button @click="moveOutVisible = false; moveOutStep = 'confirm'">关闭</el-button>
                <el-button v-if="moveOutStep === 'confirm'" type="primary" @click="moveOutExecute"
                    :loading="moveOutLoading">确认移出</el-button>
            </span>
        </el-dialog>

        <!-- 父体配置弹窗 -->
        <parent-config-dialog :visible.sync="dialogParentConfigVisible" :parent="searchForm.parentName"
            :parent-list="allParentList" />

        <!-- 品名配置弹窗 -->
        <pm-config-dialog :visible.sync="dialogPmConfigVisible" :parent="pmConfigMeta['本地父体']"
            :pm="pmConfigMeta['本地品名']" :shop="pmConfigMeta['店铺']" :msku="pmConfigMeta.msku" :show-warehouse="false"
            @saved="handlePmConfigSaved" />

        <!-- Auto 统一工作台：MSKU记录 / 任务面板 / 操作日志分页切换 -->
        <auto-workbench-dialog :visible.sync="dialogAutoWorkbench" :active-tab="autoWorkbenchTab"
            :parent="searchForm.parentName" :shop="searchForm.shop" :shop-options="filters"
            @tasks-loaded="onWorkbenchTasksLoaded" />

        <!-- 颜色配置弹窗 -->
        <color-translation :visible.sync="colorConfigVisible" />

        <!-- 批量导入配置弹窗 -->
        <el-dialog title="批量导入配置" :visible.sync="batchDialogVisible" width="920px"
            custom-class="au-dialog main-feature-dialog" :close-on-click-modal="false" append-to-body
            @closed="resetBatchDialog">
            <template v-if="batchStep === 'form'">
                <!-- 板块一：模板下载 -->
                <div class="batch-section">
                    <div class="batch-section-title"><i class="el-icon-download"></i> 模板下载</div>
                    <el-form label-width="120px" size="small">
                        <el-form-item label="模板类型">
                            <el-radio-group v-model="batchKind" size="small" @change="batchProductTypeChange">
                                <el-radio-button label="pm">品名配置模板</el-radio-button>
                                <el-radio-button label="parent">父体配置模板</el-radio-button>
                            </el-radio-group>
                            <div class="msku-hint" v-if="batchKind === 'parent'">
                                父体模板字段按最新模板表中 config_level=parent 的标记生成；品名模板沿用现有品名级结构
                            </div>
                        </el-form-item>
                        <el-form-item label="产品类型">
                            <el-select v-model="batchProductType" filterable placeholder="选择产品类型" style="width:320px"
                                @change="batchProductTypeChange">
                                <el-option v-for="pt in productTypes" :key="pt.value" :label="pt.label"
                                    :value="pt.value" />
                            </el-select>
                        </el-form-item>
                        <el-form-item label="下载空模板">
                            <el-button type="primary" size="small" icon="el-icon-download" :disabled="!batchProductType"
                                :loading="batchDownloading" @click="downloadBatchTemplate">
                                {{ batchProductType || '请先选择产品类型' }}.xlsx
                            </el-button>
                            <div class="msku-hint" v-if="batchKind === 'pm'">模板含「本地父体 / 本地品名 /
                                店铺」及品名配置项表头；只填写需要修改的配置项，单元格空白会保留原配置。当前阶段只要求 MSKU 生成所需配置，移入/移出字段无需填写；导入仅更新本地品名配置，不影响线上商品。
                            </div>
                            <div class="msku-hint" v-else>模板含「本地父体」及父体级公用字段（品牌/描述/五点等），品名自动继承；只填写需要修改的配置项。导入仅更新父体配置。
                            </div>
                        </el-form-item>
                    </el-form>
                </div>
                <!-- 板块二：导入配置 -->
                <div class="batch-section">
                    <div class="batch-section-title"><i class="el-icon-upload2"></i> 导入配置</div>
                    <el-form label-width="120px" size="small">
                        <el-form-item label="上传文件">
                            <el-upload :show-file-list="false" accept=".xlsx" :http-request="batchUploadRequest"
                                :disabled="!batchProductType">
                                <el-button size="small" type="success" plain :disabled="!batchProductType">上传并预览
                                </el-button>
                            </el-upload>
                            <div class="msku-hint">上传后先预览解析结果，确认无误再执行导入；模板类型与产品类型需与文件一致。</div>
                        </el-form-item>
                    </el-form>
                </div>
            </template>
            <template v-else-if="batchStep === 'preview'">
                <el-alert v-if="batchErrors.length" :title="'校验提示 ' + batchErrors.length + ' 条'" type="warning"
                    :closable="false" show-icon style="margin-bottom:12px;" />
                <el-alert v-else-if="!batchRows.length" title="未解析到有效数据行" type="info" :closable="false" show-icon
                    style="margin-bottom:12px;" />
                <el-table v-if="batchRows.length" :data="batchRows" size="small" max-height="420" border stripe>
                    <el-table-column prop="本地父体" label="本地父体" min-width="130" />
                    <el-table-column v-if="batchKind !== 'parent'" prop="本地品名" label="本地品名" min-width="130" />
                    <el-table-column v-if="batchKind !== 'parent'" prop="店铺" label="店铺" width="120" />
                    <el-table-column label="更新字段数" width="90" align="center">
                        <template slot-scope="scope">{{ Object.keys(scope.row.values || {}).length }}</template>
                    </el-table-column>
                    <el-table-column label="字段值预览" min-width="300">
                        <template slot-scope="scope">
                            <span style="font-size:12px;color:#909399;">{{ formatBatchValues(scope.row.values) }}</span>
                        </template>
                    </el-table-column>
                </el-table>
                <div v-if="batchErrors.length" style="margin-top:10px;max-height:150px;overflow-y:auto;">
                    <p v-for="(e, i) in batchErrors" :key="i" style="font-size:12px;color:#E6A23C;margin:2px 0;">{{ e }}
                    </p>
                </div>
            </template>
            <template v-else-if="batchStep === 'result'">
                <el-alert v-if="batchSummary" :title="'执行完成：成功 ' + batchSummary.ok + ' / 失败 ' + batchSummary.fail"
                    :type="batchSummary.fail ? 'warning' : 'success'" :closable="false" show-icon
                    style="margin-bottom:12px;" />
                <el-table :data="batchResults" size="small" max-height="420" border stripe>
                    <el-table-column prop="index" label="#" width="50" align="center" />
                    <el-table-column prop="本地父体" label="本地父体" min-width="130" />
                    <el-table-column prop="本地品名" label="本地品名" min-width="130" />
                    <el-table-column prop="店铺" label="店铺" width="110" />
                    <el-table-column label="结果" width="80" align="center">
                        <template slot-scope="scope">
                            <el-tag :type="scope.row.success ? 'success' : 'danger'" size="mini">
                                {{ scope.row.success ? '成功' : '失败' }}</el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column prop="error" label="失败原因" min-width="220" show-overflow-tooltip />
                </el-table>
            </template>
            <span slot="footer">
                <template v-if="batchStep === 'preview'">
                    <el-button @click="batchStep = 'form'">返回</el-button>
                    <el-button type="primary" :disabled="!batchRows.length" :loading="batchExecuting"
                        @click="executeBatch">确认执行（{{ batchRows.length }} 行）</el-button>
                </template>
                <template v-else>
                    <el-button @click="batchDialogVisible = false">关闭</el-button>
                </template>
            </span>
        </el-dialog>

        <!-- 生成品名待填模板弹窗：应用外层品名列表勾选的数据 -->
        <el-dialog title="生成品名待填模板" :visible.sync="fillTplVisible" width="560px" custom-class="au-dialog"
            :close-on-click-modal="false" append-to-body>
            <el-alert type="info" :closable="false" show-icon style="margin-bottom:12px;"
                :title="'将按品名列表勾选的 ' + tableSelection.length + ' 个品名（涉及 ' + fillTplParentCount + ' 个父体）生成待填模板'">
            </el-alert>
            <el-form label-width="120px" size="small">
                <el-form-item label="产品类型">
                    <el-select v-model="fillTplProductType" filterable placeholder="选择产品类型" style="width:320px">
                        <el-option v-for="pt in productTypes" :key="pt.value" :label="pt.label" :value="pt.value" />
                    </el-select>
                </el-form-item>
            </el-form>
            <div class="msku-hint">模板标识列与当前配置值已预填；直接修改需要变更的单元格，空白项上传后保留原配置。生成后可在「批量导入配置」中上传导入。</div>
            <span slot="footer">
                <el-button @click="fillTplVisible = false">关闭</el-button>
                <el-button type="primary" icon="el-icon-document-add" :loading="fillDownloading"
                    @click="downloadFilledTemplate">生成待填模板</el-button>
            </span>
        </el-dialog>

        <!-- 父体待填模板弹窗：加载全部父体供多选 -->
        <el-dialog title="父体待填模板" :visible.sync="fillParentTplVisible" width="560px"
            custom-class="au-dialog main-feature-dialog" :close-on-click-modal="false" append-to-body
            @closed="resetFillParentDialog">
            <el-alert type="info" :closable="false" show-icon style="margin-bottom:12px;"
                :title="'勾选需要生成待填模板的父体，已选 ' + fillParents.length + ' 个'">
            </el-alert>
            <div class="fill-parent-table">
                <el-input v-model="fillParentFilter" size="small" clearable prefix-icon="el-icon-search"
                    placeholder="搜索父体" style="width:240px;margin-bottom:8px;" />
                <el-table ref="fillParentTable" :data="filteredFillParents" size="small" height="280" border stripe
                    @selection-change="onFillParentSelectionChange">
                    <el-table-column type="selection" width="50" />
                    <el-table-column prop="text" label="父体" min-width="260" />
                </el-table>
            </div>
            <el-form label-width="120px" size="small" style="margin-top:12px;">
                <el-form-item label="产品类型">
                    <el-select v-model="fillTplProductType" filterable placeholder="选择产品类型" style="width:320px">
                        <el-option v-for="pt in productTypes" :key="pt.value" :label="pt.label" :value="pt.value" />
                    </el-select>
                </el-form-item>
            </el-form>
            <div class="msku-hint">父体模板标识列与当前配置值已预填；直接修改需要变更的单元格，空白项上传后保留原配置。生成后可在「批量导入配置」中上传导入。</div>
            <span slot="footer">
                <el-button @click="fillParentTplVisible = false">关闭</el-button>
                <el-button type="primary" icon="el-icon-document-add" :loading="fillDownloading"
                    @click="downloadFilledTemplate('parent')">生成父体待填模板</el-button>
            </span>
        </el-dialog>

        <!-- 批量更新MSKU弹窗（模板上传，仅覆盖 msku，保留其它字段） -->
        <el-dialog title="批量更新MSKU" :visible.sync="mskuBatchVisible" width="860px" custom-class="au-dialog"
            :close-on-click-modal="false" append-to-body @closed="resetMskuBatch">
            <template v-if="mskuBatchStep === 'form'">
                <el-alert type="info" :closable="false" show-icon
                    title="模板仅需填写：本地父体 / 本地品名 / 店铺 / msku 四列。批量更新只覆盖当前售卖MSKU，不影响 parent_sku、变体主题、产品类型等字段；品名尚未关联时会自动创建关联。" />
                <div style="margin:16px 0;text-align:center;">
                    <el-button type="primary" icon="el-icon-download" :loading="mskuBatchDownloading"
                        @click="downloadMskuTemplate">下载模板（自动更新MSKU模板.xlsx）</el-button>
                </div>
                <el-form label-width="100px" size="small">
                    <el-form-item label="上传文件">
                        <el-upload :show-file-list="false" accept=".xlsx" :http-request="mskuUploadRequest">
                            <el-button size="small" type="success" plain :loading="mskuBatchUploading">上传并预览</el-button>
                        </el-upload>
                    </el-form-item>
                </el-form>
            </template>
            <template v-else-if="mskuBatchStep === 'preview'">
                <el-alert v-if="mskuBatchErrors.length" :title="'校验提示 ' + mskuBatchErrors.length + ' 条'" type="warning"
                    :closable="false" show-icon style="margin-bottom:12px;" />
                <el-alert v-else :title="'解析到 ' + mskuBatchRows.length + ' 行有效数据，确认后执行'" type="success"
                    :closable="false" show-icon style="margin-bottom:12px;" />
                <el-table v-if="mskuBatchRows.length" :data="mskuBatchRows" size="small" max-height="420" border stripe>
                    <el-table-column prop="index" label="#" width="60" align="center" />
                    <el-table-column prop="本地父体" label="本地父体" min-width="150" />
                    <el-table-column prop="本地品名" label="本地品名" min-width="150" />
                    <el-table-column prop="店铺" label="店铺" width="130" />
                    <el-table-column prop="msku" label="msku" min-width="180" />
                    <el-table-column label="校验" width="160" align="center">
                        <template slot-scope="scope">
                            <el-tag :type="scope.row.error ? 'danger' : 'success'" size="mini">
                                {{ scope.row.error ? '异常' : 'OK' }}</el-tag>
                        </template>
                    </el-table-column>
                </el-table>
                <div v-if="mskuBatchErrors.length" style="margin-top:10px;max-height:150px;overflow-y:auto;">
                    <p v-for="(e, i) in mskuBatchErrors" :key="i" style="font-size:12px;color:#E6A23C;margin:2px 0;">{{
                        e }}</p>
                </div>
            </template>
            <template v-else-if="mskuBatchStep === 'result'">
                <el-alert v-if="mskuBatchSummary"
                    :title="'执行完成：成功 ' + mskuBatchSummary.ok + ' / 失败 ' + mskuBatchSummary.fail"
                    :type="mskuBatchSummary.fail ? 'warning' : 'success'" :closable="false" show-icon
                    style="margin-bottom:12px;" />
                <el-table :data="mskuBatchResults" size="small" max-height="420" border stripe>
                    <el-table-column prop="index" label="#" width="60" align="center" />
                    <el-table-column prop="本地父体" label="本地父体" min-width="150" />
                    <el-table-column prop="本地品名" label="本地品名" min-width="150" />
                    <el-table-column prop="店铺" label="店铺" width="130" />
                    <el-table-column prop="msku" label="msku" min-width="180" />
                    <el-table-column label="结果" width="90" align="center">
                        <template slot-scope="scope">
                            <el-tag :type="scope.row.success ? 'success' : 'danger'" size="mini">
                                {{ scope.row.success ? '成功' : '失败' }}</el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column label="错误" min-width="160">
                        <template slot-scope="scope">
                            <span style="font-size:12px;color:#E6A23C;">{{ scope.row.error }}</span>
                        </template>
                    </el-table-column>
                </el-table>
            </template>
            <span slot="footer">
                <el-button @click="mskuBatchVisible = false">关闭</el-button>
                <el-button v-if="mskuBatchStep === 'preview'" type="primary" :loading="mskuBatchExecuting"
                    @click="executeMskuBatch">确认更新</el-button>
            </span>
        </el-dialog>

        <!-- 创建并配对弹窗（选 SKU，走后端复合方法，无采购线） -->
        <el-dialog title="创建并配对" :visible.sync="createPairVisible" width="520px" custom-class="au-dialog"
            :close-on-click-modal="false" append-to-body>
            <div class="config-meta">
                <span class="meta-item"><span class="meta-label">本地父体</span>{{ createPairRow['本地父体'] }}</span>
                <span class="meta-item"><span class="meta-label">本地品名</span>{{ createPairRow['本地品名'] }}</span>
                <span class="meta-item"><span class="meta-label">店铺</span>{{ createPairRow['店铺'] }}</span>
            </div>
            <el-form label-width="110px" size="small">
                <el-form-item label="关联SKU" required>
                    <el-select v-model="createPairSku" filterable placeholder="选择要关联的 SKU" style="width:100%">
                        <el-option v-for="s in createPairSkus" :key="s" :label="s" :value="s" />
                    </el-select>
                    <div class="msku-hint">取自该品名关联的 SKU（amazon_form.male_sku）；将按品名配置生成新 MSKU 并创建后自动配对</div>
                </el-form-item>
                <el-form-item label="MSKU后缀">
                    <el-tag type="info">固定 -T（发货）</el-tag>
                    <div class="msku-hint">“创建产品并配对”只创建发货产品；跟卖请使用单独的“创建跟卖”操作。</div>
                </el-form-item>
            </el-form>
            <el-alert v-if="createPairError" :title="createPairError" type="error" :closable="false" show-icon
                style="margin-bottom:10px;" />
            <span slot="footer">
                <el-button @click="createPairVisible = false">取消</el-button>
                <el-button type="primary" :disabled="!createPairSku" :loading="createPairLoading"
                    @click="submitCreatePair">提交创建并配对</el-button>
            </span>
        </el-dialog>

        <!-- 按 SKU 识别弹窗：仅负责接收 Excel 复制的 SKU 列，识别后进入原有批量生成弹窗。 -->
        <el-dialog title="按 SKU 识别生成MSKU" :visible.sync="genMultiSkuImportVisible" width="620px"
            custom-class="au-dialog" :close-on-click-modal="false" append-to-body
            @closed="resetGenMultiSkuImport">
            <el-alert type="info" :closable="false" show-icon style="margin-bottom:12px;"
                title="请先选择店铺和本地父体，再从 Excel 复制整列 SKU 粘贴到输入框；识别后会自动带出关联品名和品名配置。" />
            <el-form label-width="72px" size="small" style="margin-bottom:10px;">
                <el-form-item label="店铺" required style="margin-bottom:10px;">
                    <el-select v-model="genMultiSkuImportShop" filterable clearable placeholder="请选择店铺"
                        style="width:100%;">
                        <el-option v-for="item in filters" :key="'gen-sku-shop-' + item.value" :label="item.text"
                            :value="item.value" />
                    </el-select>
                </el-form-item>
                <el-form-item label="本地父体" required style="margin-bottom:0;">
                    <el-select v-model="genMultiSkuImportParent" filterable clearable placeholder="请选择本地父体"
                        style="width:100%;">
                        <el-option v-for="p in parentOptions" :key="'gen-sku-parent-' + p.value" :label="p.text"
                            :value="p.value" />
                    </el-select>
                </el-form-item>
            </el-form>
            <el-input v-model="genMultiSkuText" type="textarea" :rows="8"
                placeholder="请从 Excel 复制 SKU 列并粘贴到这里，每行一个 SKU"
                @input="updateGenMultiSkuPasteCount" @paste.native="onGenMultiSkuPaste" />
            <div class="gen-sku-import-actions">
                <span class="msku-hint">已识别 {{ genMultiSkuPasteCount }} 个非空 SKU。首行“SKU/本地SKU/关联SKU”表头会自动忽略；识别范围：{{ genMultiSkuImportParent || '未选择父体' }} / {{ genMultiSkuImportShop || '未选择店铺' }}。</span>
            </div>
            <span slot="footer">
                <el-button @click="genMultiSkuImportVisible = false">取消</el-button>
                <el-button type="primary" :loading="genMultiResolveLoading"
                    :disabled="!genMultiSkuPasteCount || !genMultiSkuImportShop || !genMultiSkuImportParent"
                    @click="resolveGenMultiSkus">识别并带出配置</el-button>
            </span>
        </el-dialog>

        <!-- 批量生成MSKU弹窗（默认原有品名表格；未选品名时可从顶部按 SKU 识别） -->
        <el-dialog title="批量生成MSKU" :visible.sync="genMultiVisible" width="1240px"
            custom-class="au-dialog main-feature-dialog" :close-on-click-modal="false" append-to-body
            @closed="resetGenMulti">
            <el-alert type="info" :closable="false" show-icon style="margin-bottom:12px;"
                :title="genMultiSkuTip" />
            <div class="gen-multi-toolbar">
                <span v-if="!genMultiRows.length" class="gen-multi-empty">未选择品名</span>
                <span v-else></span>
                <el-button type="primary" plain size="small" @click="openGenMultiSkuImport">
                    按 SKU 识别生成 MSKU
                </el-button>
            </div>
            <el-form :inline="true" size="small" style="margin-bottom:8px;" @submit.native.prevent>
                <el-form-item label="后缀">
                    <el-radio-group v-model="genMultiSuffix" size="small" @change="loadGenMultiConfigs">
                        <el-radio-button label="T">T（创建发货产品）</el-radio-button>
                        <el-radio-button label="C">C（创建跟卖）</el-radio-button>
                        <el-radio-button label="CUSTOM">自定义</el-radio-button>
                    </el-radio-group>
                    <el-input v-if="genMultiSuffix === 'CUSTOM'" v-model="genMultiCustomSuffix" maxlength="6"
                        placeholder="如 HB" style="width:120px;margin-left:8px;" @input="loadGenMultiConfigs" />
                </el-form-item>
                <el-form-item label="统一数量">
                    <el-input-number v-model="genMultiDefaultNum" :min="1" :max="200" size="small" :controls="false"
                        style="width:80px;" @change="v => genMultiRows.forEach(r => r.num = v)" />
                </el-form-item>
            </el-form>
            <el-table :data="genMultiRows" size="mini" max-height="320" border stripe v-loading="genMultiCfgLoading">
                <el-table-column type="index" label="#" width="44" align="center" />
                <!-- 固定列宽与批量生成弹窗的设计稿保持一致，避免内容变化时各列被重新分配。 -->
                <el-table-column prop="parent" label="本地父体" width="128" show-overflow-tooltip />
                <el-table-column prop="pm" label="本地品名" width="192" show-overflow-tooltip />
                <el-table-column prop="shop" label="店铺" width="103" />
                <el-table-column label="关联 SKU" width="150">
                    <template slot-scope="scope">
                        <el-select v-if="genMultiIsShipping && scope.row.skus && scope.row.skus.length > 1"
                            v-model="scope.row.sku" filterable size="mini" style="width:128px;"
                            :class="scope.row._skuError || scope.row.resolveError ? 'gen-sku-error' : ''"
                            placeholder="请选择 SKU" @change="onGenRowSkuChange(scope.row)">
                            <el-option v-for="sku in scope.row.skus" :key="sku" :label="sku" :value="sku" />
                        </el-select>
                        <el-input v-else-if="genMultiIsShipping" v-model="scope.row.sku" size="mini" maxlength="128"
                            :class="scope.row._skuError || scope.row.resolveError ? 'gen-sku-error' : ''" placeholder="请输入 SKU"
                            @input="onGenRowSkuInput(scope.row)" />
                        <span v-else-if="scope.row.sku" style="font-size:12px;color:#606266;">{{ scope.row.sku }}</span>
                        <span v-else style="font-size:12px;color:#909399;">不需要</span>
                        <div v-if="scope.row.resolveError" class="gen-sku-error-message"
                            :title="scope.row.resolveError">{{ scope.row.resolveError }}</div>
                    </template>
                </el-table-column>
                <el-table-column label="生成模式" width="122" align="center">
                    <template slot-scope="scope">
                        <el-select v-model="scope.row.mode" size="mini" style="width:100px;"
                            @change="onGenRowModeChange(scope.row)">
                            <el-option label="按模板" value="template" />
                            <el-option label="按属性" value="attribute" />
                        </el-select>
                    </template>
                </el-table-column>
                <el-table-column label="基础名预览" width="253">
                    <template slot-scope="scope">
                        <div v-if="scope.row._baseEditing" style="display:flex;align-items:center;gap:4px;">
                            <el-input v-model="scope.row.base_override" size="mini" maxlength="255" placeholder="编辑基础名"
                                @keyup.enter.native="$set(scope.row, '_baseEditing', false)" />
                            <el-button type="primary" size="mini" icon="el-icon-check"
                                @click="$set(scope.row, '_baseEditing', false)" />
                        </div>
                        <div v-else
                            :style="{ fontSize: '12px', wordBreak: 'break-all', fontWeight: genRowOverlong(scope.row) ? 'bold' : 'normal' }"
                            :class="genRowOverlong(scope.row) ? 'gen-base-warn' : 'gen-base-ok'">
                            <span>{{ genRowBase(scope.row) || '-' }}</span>
                            <el-button type="text" size="mini" style="padding:0;margin-left:4px;"
                                @click="startGenRowEdit(scope.row)">编辑</el-button>
                        </div>
                        <div v-if="genRowOverlong(scope.row)" style="font-size:11px;color:#CC0000;">
                            超出最大 {{ scope.row.max_base_len }} 字符，将截断为「{{
                                (genRowBase(scope.row) || '').slice(0, scope.row.max_base_len || 0) }}」
                        </div>
                    </template>
                </el-table-column>
                <el-table-column label="数量" width="124" align="center">
                    <template slot-scope="scope">
                        <el-input-number v-model="scope.row.num" :min="1" :max="200" size="mini" :controls="false"
                            style="width:100px;" />
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="110" align="center">
                    <template slot-scope="scope">
                        <el-button type="text" size="mini" @click="openGenRowConfig(scope.row)">配置</el-button>
                        <el-button type="text" size="mini" style="color:#F56C6C;"
                            @click="genMultiRows.splice(scope.$index, 1)">移除</el-button>
                    </template>
                </el-table-column>
            </el-table>
            <el-alert v-if="!genMultiCfgLoading && genMultiConfigMissingList.length" type="warning" :closable="false"
                show-icon style="margin-top:10px;">
                <template slot="title">
                    有 {{ genMultiConfigMissingList.length }} 个品名的 MSKU 配置不完整，请点击对应行的“配置”补充后再生成；
                    也可以先切换生成模式。移入/移出字段不参与本阶段生成。
                </template>
            </el-alert>
            <div v-if="genMultiResults.length" style="margin-top:14px;">
                <!-- 汇总：失败（缺配置）+ 截断，一眼看出哪几个品名缺什么 -->
                <el-alert v-if="genMultiFailedList.length" type="error" :closable="false" show-icon
                    style="margin-bottom:10px;">
                    <template slot="title">
                        {{ genMultiFailedList.length }} 个品名缺少配置，无法生成：
                        <div v-for="(f, i) in genMultiFailedList" :key="'f' + i"
                            style="font-size:12px;font-weight:normal;color:#F56C6C;margin-top:2px;">
                            {{ f.pm }}（{{ f.parent }} / {{ f.shop }}）：{{ f.error }}
                        </div>
                    </template>
                </el-alert>
                <el-alert v-if="genMultiTruncList.length" type="warning" :closable="false" show-icon
                    style="margin-bottom:10px;">
                    <template slot="title">
                        {{ genMultiTruncList.length }} 个品名基础名超长被截断：
                        <div v-for="(t, i) in genMultiTruncList" :key="'t' + i"
                            style="font-size:12px;font-weight:normal;color:#E6A23C;margin-top:2px;">
                            {{ t.pm }}：基础名 {{ t.base_len }} 字符超出最大 {{ t.max_base_len }} 字符
                        </div>
                    </template>
                </el-alert>
                <div v-for="(r, i) in genMultiResults" :key="i"
                    style="border:1px solid #ebeef5;border-radius:4px;padding:8px 10px;margin-bottom:8px;">
                    <div style="display:flex;align-items:center;flex-wrap:wrap;gap:6px 14px;font-size:13px;">
                        <b>{{ r.pm }}</b>
                        <span style="color:#909399;">{{ r.parent }} / {{ r.shop }}</span>
                        <el-tag v-if="r.sku" size="mini" type="warning" effect="plain">SKU: {{ r.sku }}</el-tag>
                        <el-tag v-if="r.mode" size="mini" type="info" effect="plain">
                            {{ r.mode === 'attribute' ? '按属性' : '按模板' }}</el-tag>
                        <el-tag v-if="r.success" size="mini" type="success">{{ (r.mskus || []).length }} 个</el-tag>
                        <el-tag v-else size="mini" type="danger">失败</el-tag>
                    </div>
                    <div v-if="!r.success" style="font-size:12px;color:#F56C6C;margin-top:4px;">{{ r.error }}</div>
                    <template v-else>
                        <div v-if="i === 0 && genMultiSavedCount" style="font-size:12px;color:#67C23A;margin-top:4px;">
                            已保存 {{ genMultiSavedCount }} 条 Auto 记录
                        </div>
                        <div v-if="r.truncated" style="font-size:12px;color:#CC0000;font-weight:bold;margin-top:4px;">
                            当前生成模式基础名超长已被截断：基础名 {{ r.base_len }} 字符超出最大 {{ r.max_base_len }}
                            字符（{{ r.base }} → {{ (r.base || '').slice(0, r.max_base_len) }}），请查看配置字符是否超出
                        </div>
                        <div v-else style="font-size:12px;color:#409eff;margin-top:4px;">
                            基础名: {{ r.base }}（共 {{ (r.mskus || []).length }} 个）
                        </div>
                        <div style="max-height:120px;overflow-y:auto;margin-top:6px;">
                            <el-tag v-for="m in r.mskus" :key="m" size="small" :type="r.truncated ? 'danger' : ''"
                                style="margin:2px 4px 2px 0;">{{ m }}</el-tag>
                        </div>
                    </template>
                </div>
            </div>
            <span slot="footer">
                <el-button @click="genMultiVisible = false">关闭</el-button>
                <el-button v-if="genMultiResults.length" type="success" :loading="genMultiExporting"
                    @click="exportGenMulti">导出
                    Excel</el-button>
                <el-badge :value="genMultiTotalMskus" :hidden="!genMultiTotalMskus" type="warning"
                    style="margin-left:10px;">
                    <el-button type="primary" :loading="genMultiLoading"
                        :disabled="!genMultiRows.length || genMultiCfgLoading || genMultiConfigMissingList.length > 0 || genMultiResolveErrorList.length > 0"
                        @click="executeGenMulti">{{ genMultiResults.length ? '重新生成并保存' : '生成并保存 MSKU' }}</el-button>
                </el-badge>
                <span v-if="genMultiRows.length" style="margin-left:12px;font-size:12px;color:#909399;">
                    {{ genMultiRows.length }} 个品名 / 共 {{ genMultiTotalMskus }} 个 MSKU
                </span>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import PmConfigDialog from './PmConfigDialog.vue';
import ParentConfigDialog from './ParentConfigDialog.vue';
import AutoWorkbenchDialog from './AutoWorkbenchDialog.vue';
import ColorTranslation from './ColorTranslation.vue';

export default {
    components: {
        PmConfigDialog, ParentConfigDialog, AutoWorkbenchDialog, ColorTranslation,
    },
    data() {
        return {
            searchForm: { shop: '', parentName: '' },
            isSending: false,
            pmFilter: '',
            tableData: [],
            allParentList: [],
            filters: [
                { text: 'CoBak-US', value: 'CoBak-US' }, { text: 'CoBak-UK', value: 'CoBak-UK' },
                { text: 'CoBak-SG', value: 'CoBak-SG' }, { text: 'CoBak-SE', value: 'CoBak-SE' },
                { text: 'CoBak-PL', value: 'CoBak-PL' }, { text: 'CoBak-NL', value: 'CoBak-NL' },
                { text: 'CoBak-MX', value: 'CoBak-MX' }, { text: 'CoBak-JP', value: 'CoBak-JP' },
                { text: 'CoBak-IT', value: 'CoBak-IT' }, { text: 'CoBak-INDIA-IN', value: 'CoBak-INDIA-IN' },
                { text: 'CoBak-FR', value: 'CoBak-FR' }, { text: 'CoBak-ES', value: 'CoBak-ES' },
                { text: 'CoBak-DE', value: 'CoBak-DE' }, { text: 'CoBak-CA', value: 'CoBak-CA' },
                { text: 'CoBak-BR', value: 'CoBak-BR' }, { text: 'CoBak Direct-BE', value: 'CoBak Direct-BE' },
                { text: 'SMIFACE-CA', value: 'SMIFACE-CA' }, { text: 'SMIFACE-US', value: 'SMIFACE-US' },
            ],
            // 移入
            moveInVisible: false, moveInStep: 'form', moveInRow: {},
            moveInLoading: false, moveInPreviewData: {}, moveInExtraValues: {},
            moveInResult: {}, moveInTaskId: '', moveInThemes: [],
            // 移出
            moveOutVisible: false, moveOutStep: 'confirm', moveOutRow: {},
            moveOutLoading: false, moveOutPreviewInfo: {}, moveOutResult: {},
            // Auto统一工作台
            dialogAutoWorkbench: false,
            autoWorkbenchTab: 'msku',
            taskList: [],
            activeTaskCount: 0,
            // 父体配置 / 品名配置
            dialogParentConfigVisible: false,
            dialogPmConfigVisible: false,
            pmConfigMeta: { '本地父体': '', '本地品名': '', '店铺': '', msku: '' },
            // 批量生成弹窗内打开品名配置时，保存后刷新对应行的生成配置
            genConfigTargetKey: '',
            // 颜色配置
            colorConfigVisible: false,
            productTypes: [],
            // 创建并配对（选 SKU，走后端复合方法，无采购线）
            createPairVisible: false,
            createPairLoading: false,
            createPairRow: {},
            createPairSku: '',
            createPairSkus: [],
            createPairError: '',
            // 批量更新
            batchDialogVisible: false,
            batchStep: 'form',
            batchKind: 'pm',
            batchProductType: '',
            batchDownloading: false,
            batchRows: [],
            batchErrors: [],
            batchExecuting: false,
            batchResults: [],
            batchSummary: null,
            // 批量更新MSKU（模板上传）
            mskuBatchVisible: false,
            mskuBatchStep: 'form',
            mskuBatchDownloading: false,
            mskuBatchUploading: false,
            mskuBatchRows: [],
            mskuBatchErrors: [],
            mskuBatchExecuting: false,
            mskuBatchResults: [],
            mskuBatchSummary: null,
            // 批量生成MSKU（表格勾选多品名）
            tableSelection: [],
            genMultiVisible: false,
            genMultiLoading: false,
            genMultiExporting: false,
            genMultiSuffix: 'T',
            genMultiCustomSuffix: '',
            genMultiDefaultNum: 10,
            genMultiCfgLoading: false,
            genMultiRows: [],
            genMultiResults: [],
            genMultiBatchId: '',
            genMultiSavedCount: 0,
            genMultiSkuImportVisible: false,
            genMultiSkuImportShop: '',
            genMultiSkuImportParent: '',
            genMultiSkuText: '',
            genMultiSkuPasteCount: 0,
            genMultiResolveLoading: false,
            // 生成待填模板（按外层品名列表勾选生成）
            fillTplVisible: false,
            fillParentTplVisible: false,
            fillParents: [],
            fillParentFilter: '',
            fillTplProductType: '',
            fillDownloading: false,
        }
    },
    computed: {
        parentOptions() {
            return (this.allParentList || []).map(p => ({
                text: p['本地父体'] || '', value: p['本地父体'] || ''
            }));
        },
        filteredTableData() {
            const kw = this.pmFilter.toLowerCase();
            return kw ? this.tableData.filter(r => (r['本地品名'] || '').toLowerCase().includes(kw)) : this.tableData;
        },
        fillTplParentCount() {
            return new Set(this.tableSelection.map(r => r['本地父体'])).size;
        },
        filteredFillParents() {
            const kw = (this.fillParentFilter || '').trim().toLowerCase();
            return kw ? this.parentOptions.filter(row => row.text.toLowerCase().includes(kw)) : this.parentOptions;
        },
        // 批量生成：缺少配置（生成失败）的品名列表
        genMultiFailedList() {
            return this.genMultiResults.filter(r => !r.success);
        },
        // 批量生成：基础名超长被截断的品名列表
        genMultiTruncList() {
            return this.genMultiResults.filter(r => r.success && r.truncated);
        },
        // 批量生成：配置未准备好时禁止提交，避免生成后只有半批记录
        genMultiConfigMissingList() {
            return this.genMultiRows.filter(r => !r.resolveError
                && (r.cfgError || !this.genRowConfiguredBase(r) || !r.supplier));
        },
        // SKU 反查失败的行禁止提交，但错误只在“关联 SKU”列内展示。
        genMultiResolveErrorList() {
            return this.genMultiRows.filter(r => r.resolveError);
        },
        // 只有 T 后缀代表创建发货产品，需要填写关联 SKU 并进入自动配对监控。
        genMultiIsShipping() {
            return (this.effectiveGenMultiSuffix() || '').trim().toUpperCase() === 'T';
        },
        genMultiSkuTip() {
            const common = '每行会带出当前品名配置；关联 SKU 会自动填入“关联 SKU”列，未关联时也会在该列提示；生成模式可在本窗口逐行切换。';
            const skuTip = this.genMultiIsShipping
                ? '当前为 T（创建发货产品），请填写关联 SKU；'
                : '当前为非 T 后缀，只创建 MSKU 记录，不需要关联 SKU，也不会自动配对；';
            return common + skuTip
                + '如果颜色为空或需要修改，请点击该行“配置”进入弹窗确认。生成成功后会为每条 MSKU 保存一条 Auto 记录。基础名超长时可点击编辑，固定拼接段仍由系统生成。';
        },
        // 批量生成：总计要生成的 MSKU 数量（数量角标）
        genMultiTotalMskus() {
            return this.genMultiRows.reduce((s, r) => s + (Number(r.num) || 0), 0);
        }
    },
    mounted() {
        this.loadInitData();
    },
    methods: {
        statusType(s) {
            const m = {
                '待操作': 'info', '正在移入': '', '已移入': 'success',
                '正在移出': 'warning', '已移出': 'info',
            };
            return m[s] || 'info';
        },
        tableRowClass() {
            return '';
        },
        // 页面初始化：加载全部父体列表
        loadInitData() {
            this.$axios.get('/vue/auto_update/seller_asin_select', { params: {} }).then(res => {
                if (res.msg === 'success') {
                    this.allParentList = res.data || [];
                }
            }).catch(() => { });
        },
        onShopChange() { /* 切换店铺 */ },
        // 查询：选中父体 + 店铺 → 拉品名 + 合并 MSKU
        handleSearch() {
            const shop = this.searchForm.shop;
            const pname = this.searchForm.parentName;
            if (!shop) return alert('请先选择店铺');
            if (!pname) return alert('请先选择本地父体');
            this.loadTableData(shop, pname);
        },
        resetSearch() {
            this.searchForm = { shop: '', parentName: '' };
            this.tableData = [];
        },
        loadTableData(shop, pname) {
            this.isSending = true;
            this.tableData = [];
            this.$axios.post('/vue/auto_update/select_child',
                { name: pname, shop: shop }
            ).then(res => {
                if (res.msg !== 'success' || !res.data) { this.isSending = false; return; }
                const allRows = res.data;
                // 并行拉取：MSKU 关联 + 品名配置状态（父体/品名是否已配置）
                Promise.all([
                    this.$axios.post('/vue/auto_update/get_msku_rel',
                        { name: pname, shop: shop }),
                    this.$axios.get('/vue/vue_product/operation-config/template-list',
                        { params: { parent: pname, shop: shop } }),
                ]).then(([res2, resC]) => {
                    const mskuMap = {};
                    if (res2.msg === 'success' && res2.data) {
                        res2.data.forEach(r => { mskuMap[r['本地父体'] + '|' + r['本地品名']] = r; });
                    }
                    const pmStatus = {};
                    if (resC.code === 200 && resC.data) {
                        resC.data.forEach(r => { pmStatus[r['本地品名']] = r.config_status; });
                    }
                    allRows.forEach(row => {
                        const key = row['本地父体'] + '|' + row['本地品名'];
                        const rel = mskuMap[key];
                        row._hasRel = !!rel;                    // 是否有关联记录：false=未同步(/)
                        row.msku = rel ? rel.msku || '' : '';
                        row['状态'] = rel ? rel['状态'] || '待操作' : '待操作';
                        row.product_type = rel ? rel.product_type || '' : '';
                        row['品名配置'] = pmStatus[row['本地品名']] || '未配置';
                    });
                    this.tableData = allRows;
                    this.isSending = false;
                }).catch(() => {
                    // 配置状态拉取失败不阻断列表展示
                    allRows.forEach(row => {
                        row['品名配置'] = '未配置';
                    });
                    this.tableData = allRows;
                    this.isSending = false;
                });
            }).catch(() => { this.isSending = false; });
        },
        // === 移入 ===
        openMoveIn(row) {
            // 移入前置校验：当前记录已有售卖MSKU → 需先移出
            if (row.msku) {
                this.$message.warning('该品名已有售卖MSKU（' + row.msku + '），需先执行移出操作');
                return;
            }
            this.moveInRow = { ...row, new_msku: '' }; this.moveInStep = 'form';
            this.moveInPreviewData = {}; this.moveInExtraValues = {};
            this.moveInResult = {}; this.pollResult = null; this.moveInThemes = [];
            if (!this.moveInRow['店铺']) this.moveInRow['店铺'] = this.searchForm.shop || '';
            if (!this.productTypes.length) this.fetchProductTypes();
            // 新MSKU按配置自动生成（-C 后缀），可修改
            this.loadMoveInPrepare(row);
            // Parent SKU / 产品类型 / Variation Theme 从品名配置自动带入（可修改），变体主题候选按产品类型加载
            this.$axios.get('/vue/vue_product/operation-config', {
                params: { parent: row['本地父体'], pm: row['本地品名'], shop: row['店铺'] || this.searchForm.shop }
            }).then(res => {
                if (res.code === 200 && res.data) {
                    if (res.data.parent_sku) this.moveInRow.parent_sku = res.data.parent_sku;
                    if (res.data.variation_theme) this.moveInRow.variation_theme = res.data.variation_theme;
                    this.moveInRow.product_type = res.data.product_type || row.product_type || '';
                    this.loadMoveInThemes(this.moveInRow.product_type);
                } else {
                    this.moveInRow.product_type = row.product_type || '';
                    this.loadMoveInThemes(this.moveInRow.product_type);
                }
            }).catch(() => {
                this.moveInRow.product_type = row.product_type || '';
                this.loadMoveInThemes(this.moveInRow.product_type);
            });
            this.moveInVisible = true;
        },
        // 生成移入新MSKU（后缀 -C）：按父体/品名配置的 MSKU 生成模式
        loadMoveInPrepare(row) {
            this.$axios.post('/vue/vue_product/create/prepare', {
                parent: row['本地父体'], pm: row['本地品名'], shop: row['店铺'] || this.searchForm.shop, suffix: 'C',
            }).then(res => {
                if (res.code === 200 && res.data && res.data.success) {
                    this.moveInRow.new_msku = res.data.msku || '';
                    if (res.data.msku_error) {
                        this.$message.warning('生成移入MSKU失败：' + res.data.msku_error);
                    }
                } else {
                    this.$message.error((res.data && res.data.error) || res.message || '生成移入MSKU失败');
                }
            }).catch(() => { });
        },
        onMoveInProductTypeChange() {
            // 产品类型变化后，变体主题候选随之变化，重置已选的变体主题与补充值
            this.moveInRow.variation_theme = '';
            this.moveInExtraValues = {};
            this.loadMoveInThemes(this.moveInRow.product_type);
        },
        loadMoveInThemes(productType) {
            if (!productType) { this.moveInThemes = []; return; }
            this.$axios.get('/vue/vue_product/variation-themes', {
                params: { product_type: productType }
            }).then(res => {
                if (res.code === 200 && res.data) this.moveInThemes = res.data;
            }).catch(() => { });
        },
        // 预览后返回表单步骤（保留已填内容，仅清空旧预览结果）
        backToMoveInForm() {
            this.moveInPreviewData = {};
            this.moveInStep = 'form';
        },
        moveInPreview() {
            if (!this.moveInRow.msku) return alert('请输入源MSKU');
            if (!this.moveInRow.parent_sku) return alert('请输入要移入的父体 MSKU');
            if (!this.moveInRow.variation_theme) return alert('请选择变体主题');
            this.moveInLoading = true;
            this.$axios.post('/vue/auto_update/move_in_preview', {
                shop: this.moveInRow['店铺'], msku: this.moveInRow.msku,
                parent_sku: this.moveInRow.parent_sku, variation_theme: this.moveInRow.variation_theme,
            }).then(res => {
                this.moveInLoading = false;
                if (res.msg === 'success') {
                    this.moveInPreviewData = res.data;
                    if (res.data.missing_fields) {
                        res.data.missing_fields.forEach(mf => {
                            if (!(mf.field_name in this.moveInExtraValues))
                                this.$set(this.moveInExtraValues, mf.field_name, '');
                        });
                    }
                    this.moveInStep = 'preview';
                } else alert('预览失败: ' + (res.message || ''));
            }).catch(e => { this.moveInLoading = false; alert('请求失败: ' + e); });
        },
        moveInExecute() {
            if (!this.moveInRow.new_msku) return alert('请输入新MSKU');
            this.moveInLoading = true;
            this.$axios.post('/vue/auto_update/move_in_execute', {
                shop: this.moveInRow['店铺'], msku: this.moveInRow.msku,
                new_msku: this.moveInRow.new_msku.trim(),
                parent_sku: this.moveInRow.parent_sku, variation_theme: this.moveInRow.variation_theme,
                variation_field_values: this.moveInExtraValues,
                '本地父体': this.moveInRow['本地父体'], '本地品名': this.moveInRow['本地品名'],
            }).then(res => {
                this.moveInLoading = false;
                if (res.msg === 'success') {
                    this.moveInResult = res.data; this.moveInTaskId = res.data.task_id;
                    this.moveInStep = 'executing';
                    // 主表刷新；任务状态由后端处理，任务面板打开时再读取。
                    this.refreshTableData();
                    // 保留本地未完成数量提示，不在前端轮询任务状态。
                    if (res.data.task_id) {
                        this.taskList.unshift({
                            task_id: res.data.task_id,
                            new_msku: this.moveInRow.new_msku.trim(),
                            action: 'move_in',
                            status: 'publishing',
                            status_msg: '提交成功',
                            step: '已提交移入刊登，等待Amazon处理',
                        });
                        this._updateActiveCount();
                    }
                } else alert('移入失败: ' + (res.message || ''));
            }).catch(e => { this.moveInLoading = false; alert('请求失败: ' + e); });
        },
        // === 任务面板 ===
        openTaskPanel() {
            this.openAutoWorkbench('tasks');
        },
        openAutoMskuTasks() {
            this.openAutoWorkbench('msku');
        },
        openLogDialog() {
            this.openAutoWorkbench('logs');
        },
        openAutoWorkbench(tab) {
            this.autoWorkbenchTab = ['msku', 'tasks', 'logs'].indexOf(tab) >= 0 ? tab : 'msku';
            this.dialogAutoWorkbench = true;
        },
        onWorkbenchTasksLoaded(rows) {
            const all = [];
            (rows || []).forEach(row => {
                all.push(row);
                if (row.children && row.children.length) all.push(...row.children);
            });
            this.taskList = all.filter(t => t.action === 'move_in' || t.action === 'move_out');
            this._updateActiveCount();
        },
        _updateActiveCount() {
            const active = ['pending', 'publishing', 'phase1_done', 'phase2_done', 'procuring'];
            this.activeTaskCount = this.taskList.filter(t => active.includes(t.status)).length;
        },
        // === 移出 ===
        openMoveOut(row) {
            this.moveOutRow = { ...row, item_name: '', title_differentiation: '' };
            this.moveOutStep = 'confirm';
            this.moveOutPreviewInfo = {}; this.moveOutResult = {};
            if (!this.moveOutRow['店铺']) this.moveOutRow['店铺'] = this.searchForm.shop || '';
            if (this.moveOutRow.msku) {
                this.$axios.post('/vue/auto_update/move_out_preview', {
                    shop: this.moveOutRow['店铺'], msku: this.moveOutRow.msku,
                }).then(res => {
                    if (res.msg === 'success') {
                        this.moveOutPreviewInfo = res.data;
                        // 带入品名/父体配置的移出标题/副标题（可修改）
                        this.moveOutRow.item_name = res.data.move_out_title || '';
                        this.moveOutRow.title_differentiation = res.data.move_out_title_differentiation || '';
                    }
                }).catch(() => { });
            }
            this.moveOutVisible = true;
        },
        // 手动输入/修改移出 MSKU 后重新加载移出预览（标题配置等）
        onMoveOutMskuChange() {
            const msku = (this.moveOutRow.msku || '').trim();
            if (!msku || !this.moveOutRow['店铺']) return;
            this.$axios.post('/vue/auto_update/move_out_preview', {
                shop: this.moveOutRow['店铺'], msku: msku,
            }).then(res => {
                if (res.msg === 'success') {
                    this.moveOutPreviewInfo = res.data;
                    this.moveOutRow.item_name = res.data.move_out_title || '';
                    this.moveOutRow.title_differentiation = res.data.move_out_title_differentiation || '';
                }
            }).catch(() => { });
        },
        moveOutExecute() {
            if (!this.moveOutRow.msku) return alert('请输入要移出的 MSKU');
            this.moveOutLoading = true;
            this.$axios.post('/vue/auto_update/move_out_execute', {
                shop: this.moveOutRow['店铺'], msku: this.moveOutRow.msku,
                item_name: this.moveOutRow.item_name, title_differentiation: this.moveOutRow.title_differentiation,
                '本地父体': this.moveOutRow['本地父体'], '本地品名': this.moveOutRow['本地品名'],
            }).then(res => {
                this.moveOutLoading = false;
                if (res.msg === 'success') {
                    this.moveOutResult = res.data; this.moveOutStep = 'executing';
                    // 主表刷新；任务状态由后端处理，任务面板打开时再读取。
                    this.refreshTableData();
                    // 保留本地未完成数量提示，不在前端轮询任务状态。
                    if (res.data.task_id) {
                        this.taskList.unshift({
                            task_id: res.data.task_id,
                            new_msku: this.moveOutRow.msku,
                            action: 'move_out',
                            status: 'publishing',
                            status_msg: '提交成功',
                            step: '已提交移出刊登，等待Amazon处理',
                        });
                        this._updateActiveCount();
                    }
                }
                else alert('移出失败: ' + (res.message || ''));
            }).catch(e => { this.moveOutLoading = false; alert('请求失败: ' + e); });
        },
        refreshTableData() {
            const shop = this.searchForm.shop;
            const pname = this.searchForm.parentName;
            if (shop && pname) {
                this.loadTableData(shop, pname);
            }
        },
        // === 操作配置（父体配置 / 品名配置）===
        fetchProductTypes() {
            this.$axios.get('/vue/vue_product/product-types').then(res => {
                if (res.code === 200 && res.data) {
                    this.productTypes = res.data;
                }
            }).catch(() => { });
        },
        openParentConfig() {
            // 父体自带店铺属性，直接弹出弹窗选择父体即可编辑
            this.dialogParentConfigVisible = true;
        },
        openConfig(row) {
            this.genConfigTargetKey = '';
            this.pmConfigMeta = {
                '本地父体': row['本地父体'] || '',
                '本地品名': row['本地品名'] || '',
                '店铺': row['店铺'] || '',
                msku: row.msku || '',
            };
            this.dialogPmConfigVisible = true;
        },
        openGenRowConfig(row) {
            if (!row) return;
            this.genConfigTargetKey = this.tableRowKey({
                '本地父体': row.parent,
                '本地品名': row.pm,
                '店铺': row.shop,
            });
            this.pmConfigMeta = {
                '本地父体': row.parent || '',
                '本地品名': row.pm || '',
                '店铺': row.shop || '',
                msku: '',
            };
            this.dialogPmConfigVisible = true;
        },
        handlePmConfigSaved() {
            this.refreshTableData();
            if (this.genConfigTargetKey && this.genMultiVisible) {
                this.loadGenMultiConfigs();
            }
            this.genConfigTargetKey = '';
        },
        // 表格「编辑」下拉命令分发
        onRowCommand(cmd, row) {
            if (cmd === 'config') return this.openConfig(row);
            // /（未同步）状态仅允许「配置」，其余操作需先关联
            if (cmd === 'create_pair') return this.openCreatePair(row);
            if (!row._hasRel) {
                this.$message.warning('该品名未同步，需先关联后才能执行该操作');
                return;
            }
            if (cmd === 'move_in') this.openMoveIn(row);
            else if (cmd === 'move_out') this.openMoveOut(row);
            else if (cmd === 'reset') this.openReset(row);
        },
        // === 重置 ===
        openReset(row) {
            this.$confirm('确定要重置该品名吗？将删除当前售卖MSKU（' + (row.msku || '-') + '），使其变为未绑定状态。',
                '重置确认', { type: 'warning', confirmButtonText: '确认重置', cancelButtonText: '取消' })
                .then(() => {
                    this.$axios.post('/vue/auto_update/reset_msku_rel', {
                        '本地父体': row['本地父体'],
                        '本地品名': row['本地品名'],
                        '店铺': row['店铺'],
                    }).then(res => {
                        if (res.msg === 'success') {
                            this.$message.success('已重置为未绑定');
                            this.refreshTableData();
                        } else {
                            this.$message.error(res.message || '重置失败');
                        }
                    }).catch(() => this.$message.error('请求失败'));
                }).catch(() => { });
        },
        // === 批量更新 ===
        openBatchDialog() {
            this.batchDialogVisible = true;
            this.batchStep = 'form';
            if (!this.productTypes.length) this.fetchProductTypes();
        },
        resetBatchDialog() {
            this.batchStep = 'form';
            this.batchKind = 'pm';
            this.batchProductType = '';
            this.batchRows = [];
            this.batchErrors = [];
            this.batchResults = [];
            this.batchSummary = null;
        },
        batchProductTypeChange() {
            this.batchRows = [];
            this.batchErrors = [];
        },
        // === 生成待填模板（品名走外层勾选，父体走弹窗多选） ===
        openFillTplDialog(kind) {
            if (!this.productTypes.length) this.fetchProductTypes();
            this.fillTplProductType = '';
            if (kind === 'parent') {
                this.resetFillParentDialog();
                this.fillParentTplVisible = true;
                this.$nextTick(() => this.clearFillParentSelection());
            } else {
                if (!this.tableSelection.length) return this.$message.warning('请先在品名列表勾选数据');
                this.fillTplVisible = true;
            }
        },
        onFillParentSelectionChange(rows) {
            this.fillParents = rows.map(row => row.value);
        },
        clearFillParentSelection() {
            this.fillParents = [];
            const table = this.$refs.fillParentTable;
            if (table && typeof table.clearSelection === 'function') table.clearSelection();
        },
        resetFillParentDialog() {
            this.clearFillParentSelection();
            this.fillParentFilter = '';
            this.fillDownloading = false;
        },
        downloadFilledTemplate(kind) {
            if (!this.fillTplProductType) return this.$message.warning('请先选择产品类型');
            let body;
            if (kind === 'parent') {
                if (!this.fillParents.length) return this.$message.warning('请勾选至少一个父体');
                body = { product_type: this.fillTplProductType, kind: 'parent', parents: this.fillParents };
            } else {
                if (!this.tableSelection.length) return this.$message.warning('请先在品名列表勾选数据');
                body = {
                    product_type: this.fillTplProductType, kind: 'pm',
                    items: this.tableSelection.map(r => ({
                        parent: r['本地父体'], pm: r['本地品名'], shop: r['店铺']
                    })),
                };
            }
            this.fillDownloading = true;
            this.$axios.post('/vue/vue_product/batch/template-fill', body, { responseType: 'blob' }).then(res => {
                const blob = new Blob([res], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = this.fillTplProductType + (kind === 'parent' ? '_父体待填' : '_待填') + '.xlsx';
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                if (kind === 'parent') {
                    this.clearFillParentSelection();
                    this.fillParentTplVisible = false;
                    this.$message.success('父体待填模板已生成');
                }
                setTimeout(() => {
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                    this.fillDownloading = false;
                }, 300);
            }).catch(() => {
                this.fillDownloading = false;
                this.$message.error('生成待填模板失败');
            });
        },
        downloadBatchTemplate() {
            if (!this.batchProductType) return this.$message.warning('请先选择产品类型');
            this.batchDownloading = true;
            const url = this.batchKind === 'parent'
                ? '/vue/vue_product/batch/parent-template'
                : '/vue/vue_product/batch/template';
            this.$axios.get(url, {
                params: { product_type: this.batchProductType },
                responseType: 'blob',
            }).then(res => {
                const blob = new Blob([res], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = this.batchProductType + '.xlsx';
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                setTimeout(() => {
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                    this.batchDownloading = false;
                }, 300);
            }).catch(() => {
                this.batchDownloading = false;
                this.$message.error('模板下载失败');
            });
        },
        batchUploadRequest(option) {
            const fd = new FormData();
            fd.append('product_type', this.batchProductType);
            fd.append('kind', this.batchKind);
            fd.append('file', option.file);
            this.$axios.post('/vue/vue_product/batch/parse', fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            }).then(res => {
                if (res.code === 200 && res.data) {
                    this.batchRows = res.data.rows || [];
                    this.batchErrors = res.data.errors || [];
                    this.batchStep = 'preview';
                    if (this.batchRows.length) this.$message.success(`解析到 ${this.batchRows.length} 行`);
                } else {
                    this.$message.error((res && res.message) || '解析失败');
                }
            }).catch(e => this.$message.error('解析失败: ' + (e && e.message ? e.message : e)));
        },
        executeBatch() {
            this.batchExecuting = true;
            this.$axios.post('/vue/vue_product/batch/execute', {
                product_type: this.batchProductType,
                kind: this.batchKind,
                rows: this.batchRows,
            }).then(res => {
                this.batchExecuting = false;
                if (res.code === 200 && res.data) {
                    this.batchResults = res.data.results || [];
                    this.batchSummary = res.data.summary || null;
                    this.batchStep = 'result';
                    this.refreshTableData();
                } else {
                    this.$message.error((res && res.message) || '执行失败');
                }
            }).catch(e => {
                this.batchExecuting = false;
                this.$message.error('执行失败: ' + e);
            });
        },
        formatBatchValues(values) {
            if (!values) return '';
            return Object.keys(values).map(k => {
                let v = values[k];
                if (v && typeof v === 'object') v = JSON.stringify(v);
                return k + '=' + v;
            }).join('；');
        },
        // === 当前售卖MSKU 单元格点击编辑 ===
        startMskuEdit(row) {
            this.$set(row, 'msku_edit', row.msku || '');
            this.$set(row, '_mskuEditing', true);
        },
        cancelMskuEdit(row) {
            this.$set(row, '_mskuEditing', false);
        },
        saveMskuEdit(row) {
            const val = (row.msku_edit || '').trim();
            if (!val) return this.$message.warning('MSKU 不能为空');
            if (!row['本地父体'] || !row['本地品名'] || !row['店铺']) {
                return this.$message.warning('该行缺少 本地父体/本地品名/店铺，无法保存');
            }
            this.$set(row, '_mskuSaving', true);
            this.$axios.post('/vue/auto_update/update_msku', {
                '本地父体': row['本地父体'],
                '本地品名': row['本地品名'],
                '店铺': row['店铺'],
                msku: val,
            }).then(res => {
                this.$set(row, '_mskuSaving', false);
                if (res.msg === 'success') {
                    row.msku = val;
                    row._hasRel = true;          // 未同步 → 关联
                    row['状态'] = row['状态'] || '待操作';
                    this.$set(row, '_mskuEditing', false);   // 保存成功后退出编辑态
                    this.$message.success('MSKU 已更新');
                } else {
                    this.$message.error(res.message || '更新失败');
                }
            }).catch(() => {
                this.$set(row, '_mskuSaving', false);
                this.$message.error('请求失败');
            });
        },
        // === 批量更新MSKU（模板上传）===
        openMskuBatch() {
            this.mskuBatchVisible = true;
            this.mskuBatchStep = 'form';
        },
        // === 批量生成MSKU（表格勾选多品名）===
        tableRowKey(row) {
            return (row['本地父体'] || '') + '|' + (row['本地品名'] || '') + '|' + (row['店铺'] || '');
        },
        onTableSelectionChange(rows) {
            this.tableSelection = rows || [];
        },
        clearTableSelection() {
            this.tableSelection = [];
            const table = this.$refs.mainTable;
            if (table && typeof table.clearSelection === 'function') table.clearSelection();
        },
        buildGenMultiRowsFromSelected(rows) {
            // 去重（同一父体+品名+店铺只保留一条）
            const seen = new Set();
            return (rows || [])
                .map(r => ({
                    parent: r['本地父体'] || '', pm: r['本地品名'] || '', shop: r['店铺'] || '',
                    sku: '', skus: [], resolveError: '',
                    num: this.genMultiDefaultNum,
                }))
                .filter(r => {
                    const k = r.parent + '|' + r.pm + '|' + r.shop;
                    if (seen.has(k)) return false;
                    seen.add(k);
                    return r.pm && r.shop;
                });
        },
        openGenMulti() {
            this.genMultiRows = this.buildGenMultiRowsFromSelected(this.tableSelection);
            this.genMultiResults = [];
            this.genMultiVisible = true;
            // 已把主表勾选内容复制到批量生成弹窗，主表选中状态立即清空，避免后续误操作。
            this.clearTableSelection();
            if (this.genMultiRows.length) this.loadGenMultiConfigs();
        },
        openGenMultiSkuImport() {
            // 优先沿用批量生成弹窗打开前在页面顶部选择的范围；没有选择时由用户在本弹窗补充。
            this.genMultiSkuImportShop = this.searchForm.shop || '';
            this.genMultiSkuImportParent = this.searchForm.parentName || '';
            this.genMultiSkuText = '';
            this.genMultiSkuPasteCount = 0;
            this.genMultiResolveLoading = false;
            this.genMultiSkuImportVisible = true;
        },
        resetGenMultiSkuImport() {
            this.genMultiSkuImportShop = '';
            this.genMultiSkuImportParent = '';
            this.genMultiSkuText = '';
            this.genMultiSkuPasteCount = 0;
            this.genMultiResolveLoading = false;
        },
        parseGenMultiSkuText(text) {
            const lines = String(text || '').replace(/\uFEFF/g, '').split(/\r\n|\n|\r/);
            const skus = lines.map(line => {
                // Excel 复制单列时每行可能带一个制表符；只取第一列，避免把其它列拼进 SKU。
                return String(line || '').split('\t')[0].trim();
            }).filter(Boolean);
            return skus.filter((sku, index) => !(index === 0 && /^(sku|本地sku|关联sku)$/i.test(sku)));
        },
        updateGenMultiSkuPasteCount() {
            this.genMultiSkuPasteCount = this.parseGenMultiSkuText(this.genMultiSkuText).length;
        },
        onGenMultiSkuPaste(event) {
            const pasted = event && event.clipboardData
                ? event.clipboardData.getData('text/plain') : '';
            if (!pasted) return;
            // 使用剪贴板原文，保证 Excel 的逐行内容按原样解析。
            event.preventDefault();
            this.genMultiSkuText = pasted;
            this.updateGenMultiSkuPasteCount();
        },
        resolveGenMultiSkus() {
            const skus = this.parseGenMultiSkuText(this.genMultiSkuText);
            if (!skus.length) {
                this.$message.warning('请先粘贴 SKU 列');
                return;
            }
            if (!this.genMultiSkuImportShop || !this.genMultiSkuImportParent) {
                this.$message.warning('请先选择店铺和本地父体');
                return;
            }
            this.genMultiResolveLoading = true;
            this.genMultiResults = [];
            this.genMultiRows = [];
            this.$axios.post('/vue/vue_product/msku-config/by-sku/batch', {
                skus,
                parent: this.genMultiSkuImportParent,
                shop: this.genMultiSkuImportShop,
                suffix: this.effectiveGenMultiSuffix() || 'T',
            }).then(res => {
                this.genMultiResolveLoading = false;
                if (res.code !== 200 || !res.data) {
                    this.$message.error((res && res.message) || 'SKU 识别失败');
                    return;
                }
                const results = res.data.results || [];
                this.genMultiRows = results.map(r => ({
                    parent: r.parent || '', pm: r.pm || '', shop: r.shop || '',
                    sku: r.sku || '', skus: Array.isArray(r.skus) ? r.skus : (r.sku ? [r.sku] : []),
                    resolveError: r.sku_error || '',
                    // SKU 反查结果就是用户本次导入的目标 SKU，后续切换后缀重新读配置时保留它。
                    _skuManual: true, cfgError: r.sku_error ? '' : (r.success ? '' : (r.error || '')), num: this.genMultiDefaultNum,
                }));
                this.applyGenMultiConfigResults(results);
                this.genMultiSkuImportVisible = false;
                this.genMultiVisible = true;
                const unmatched = Number(res.data.unmatched_count || 0);
                const configFailed = Number(res.data.config_failed_count || 0);
                if (unmatched || configFailed) {
                    const matched = results.length - unmatched - configFailed;
                    let message = '识别完成：' + matched + ' 个成功';
                    if (unmatched) message += '，' + unmatched + ' 个未关联';
                    if (configFailed) message += '，' + configFailed + ' 个配置读取失败';
                    this.$message.warning(message + '，请查看表格提示');
                } else {
                    this.$message.success('已识别 ' + results.length + ' 个 SKU，并带出品名配置');
                }
            }).catch(() => {
                this.genMultiResolveLoading = false;
                this.$message.error('SKU 识别请求失败');
            });
        },
        // 拉取每个品名的 MSKU 生成配置（同时返回 male_sku 关联 SKU）
        loadGenMultiConfigs() {
            if (!this.genMultiRows.length) return;
            this.genMultiCfgLoading = true;
            this.$axios.post('/vue/vue_product/msku-config/batch', {
                items: this.genMultiRows.map(r => ({
                    parent: r.parent, pm: r.pm, shop: r.shop,
                })),
                suffix: this.effectiveGenMultiSuffix() || 'T',
            }).then(res => {
                this.genMultiCfgLoading = false;
                if (res.code === 200 && res.data && res.data.results) {
                    this.applyGenMultiConfigResults(res.data.results);
                } else {
                    this.$message.error((res && res.message) || '品名配置读取失败');
                }
            }).catch(() => {
                this.genMultiCfgLoading = false;
                this.$message.error('品名配置读取失败');
            });
        },
        applyGenMultiConfigResults(configResults) {
            const byKey = {};
            (configResults || []).forEach(c => {
                byKey[(c.parent || '') + '|' + (c.pm || '') + '|' + (c.shop || '')] = c;
            });
            this.genMultiRows.forEach(r => {
                const c = byKey[r.parent + '|' + r.pm + '|' + r.shop];
                if (!c) return;

                const associationError = c.sku_error
                    || (c.sku_status === '未关联 SKU' ? '未关联 SKU' : '');
                // SKU 导入失败的行即使后续切换后缀重新读配置，也要保留原始错误提示。
                const nextSkuError = associationError || (r._skuManual ? r.resolveError : '');
                const associatedSkus = Array.isArray(c.skus)
                    ? c.skus.filter(sku => String(sku || '').trim())
                    : (c.sku ? [c.sku] : []);
                this.$set(r, 'skus', associatedSkus);
                if (!r._skuManual) {
                    this.$set(r, 'sku', c.sku || '');
                }

                if (c && c.success) {
                    this.$set(r, 'cfgError', '');
                    this.$set(r, 'resolveError', nextSkuError);
                    if (!r._modeManual) this.$set(r, 'mode', c.mode || 'template');
                    this.$set(r, 'msku_template', c.msku_template || '');
                    this.$set(r, 'series', c.series || '');
                    this.$set(r, 'style', c.style || '');
                    this.$set(r, 'color', c.color || '');
                    this.$set(r, 'shell', c.shell || '');
                    this.$set(r, 'msku_separator', c.msku_separator || '-');
                    this.$set(r, 'supplier', c.supplier || '');
                    this.$set(r, 'country', c.country || '');
                    this.$set(r, 'country_abbr', c.country_abbr || '');
                    this.$set(r, 'template_base', c.template_base || '');
                    this.$set(r, 'attribute_base', c.attribute_base || '');
                    this.$set(r, 'max_base_len', c.max_base_len || 0);
                    if (!r.base_override && this.genRowConfiguredBase(r).length > (c.max_base_len || 0)) {
                        this.$set(r, 'base_override', this.genRowConfiguredBase(r));
                    }
                } else {
                    this.$set(r, 'mode', 'template');
                    this.$set(r, 'cfgError', c.error || '配置读取失败');
                    this.$set(r, 'resolveError', nextSkuError);
                }
            });
        },
        onGenRowSkuInput(row) {
            if (!row) return;
            this.$set(row, '_skuManual', true);
            this.$set(row, '_skuError', false);
            this.$set(row, 'resolveError', '');
        },
        onGenRowSkuChange(row) {
            this.onGenRowSkuInput(row);
        },
        onGenRowModeChange(row) {
            if (!row) return;
            this.$set(row, '_modeManual', true);
            // 切换模式后重新使用该模式的配置基础名，避免沿用上一模式的手工覆盖值。
            this.$set(row, 'base_override', '');
            this.$set(row, '_baseEditing', false);
        },
        // 行级基础名预览（按当前行模式）
        genRowConfiguredBase(r) {
            return r.mode === 'template' ? (r.msku_template || '') : (r.attribute_base || '');
        },
        genRowBase(r) {
            return (r.base_override || '').trim() || this.genRowConfiguredBase(r);
        },
        startGenRowEdit(r) {
            if (!r.base_override) this.$set(r, 'base_override', this.genRowConfiguredBase(r));
            this.$set(r, '_baseEditing', true);
        },
        // 行级超长预判
        genRowOverlong(r) {
            const base = this.genRowBase(r);
            return !!base && !!r.max_base_len && base.length > r.max_base_len;
        },
        resetGenMulti() {
            this.genMultiRows = [];
            this.genMultiResults = [];
            this.genMultiBatchId = '';
            this.genMultiSavedCount = 0;
            this.genMultiLoading = false;
            this.genMultiExporting = false;
            this.genMultiSuffix = 'T';
            this.genMultiCustomSuffix = '';
            this.genConfigTargetKey = '';
        },
        effectiveGenMultiSuffix() {
            return this.genMultiSuffix === 'CUSTOM' ? (this.genMultiCustomSuffix || '').trim() : this.genMultiSuffix;
        },
        executeGenMulti() {
            if (!this.genMultiRows.length) return;
            if (this.genMultiSuffix === 'CUSTOM' && !/^[A-Za-z0-9]{1,6}$/.test(this.effectiveGenMultiSuffix())) {
                this.$message.warning('MSKU 后缀需为 1-6 位英文字母或数字');
                return;
            }
            const isShipping = this.genMultiIsShipping;
            const missingSku = [];
            this.genMultiRows.forEach((row, index) => {
                const missing = isShipping && !(row.sku || '').trim();
                this.$set(row, '_skuError', missing);
                if (missing) missingSku.push(index + 1);
            });
            if (missingSku.length) {
                this.$message.warning('请填写第 ' + missingSku.join('、') + ' 行的关联 SKU');
                return;
            }
            this.genMultiLoading = true;
            this.$axios.post('/vue/vue_product/msku-generate/multi', {
                items: this.genMultiRows.map(r => ({
                    parent: r.parent, pm: r.pm, shop: r.shop,
                    // 非 T 后缀不参与配对，避免把用户残留输入的 SKU 写入记录。
                    sku: isShipping ? (r.sku || '').trim() : '',
                    mode: r.mode || 'template',
                    base_override: (r.base_override || '').trim(),
                    num: r.num,
                })),
                mode: 'auto',   // 未指定行模式时按品名自身配置生成
                suffix: this.effectiveGenMultiSuffix(),
            }).then(res => {
                this.genMultiLoading = false;
                if (res.code === 200 && res.data) {
                    this.clearTableSelection();
                    this.genMultiResults = res.data.results || [];
                    this.genMultiBatchId = res.data.batch_id || '';
                    this.genMultiSavedCount = Number(res.data.persisted_count || 0);
                    const truncated = this.genMultiResults.filter(r => r.truncated).length;
                    const failed = this.genMultiResults.filter(r => !r.success).length;
                    if (truncated) {
                        this.$message.warning(truncated + ' 个品名的配置基础名超长，MSKU 已被截断，请注意查看');
                    }
                    if (failed) {
                        this.$message.error(failed + ' 个品名生成失败，请查看原因');
                    } else if (res.data.persisted) {
                        this.$message.success('已生成并保存 ' + this.genMultiSavedCount + ' 条 Auto MSKU 记录');
                    }
                } else {
                    this.$message.error((res && res.message) || '生成失败');
                }
            }).catch(() => {
                this.genMultiLoading = false;
                this.$message.error('生成请求失败');
            });
        },
        exportGenMulti() {
            if (!this.genMultiResults.length) return;
            this.genMultiExporting = true;
            this.$axios.post('/vue/vue_product/msku-generate/multi/download', {
                rows: this.genMultiResults.map(r => ({
                    parent: r.parent, pm: r.pm, shop: r.shop,
                    sku: r.sku || '',
                    base: r.base, mskus: r.mskus || [],
                    truncated: r.truncated, base_len: r.base_len,
                    max_base_len: r.max_base_len, error: r.error || '',
                    color: r.color || {},
                })),
            }, { responseType: 'blob' }).then(res => {
                const blob = new Blob([res], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = '批量生成MSKU.xlsx';
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                setTimeout(() => {
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                    this.genMultiExporting = false;
                }, 300);
            }).catch(() => {
                this.genMultiExporting = false;
                this.$message.error('导出失败');
            });
        },
        resetMskuBatch() {
            this.mskuBatchStep = 'form';
            this.mskuBatchRows = [];
            this.mskuBatchErrors = [];
            this.mskuBatchResults = [];
            this.mskuBatchSummary = null;
            this.mskuBatchUploading = false;
        },
        downloadMskuTemplate() {
            this.mskuBatchDownloading = true;
            this.$axios.get('/vue/auto_update/msku_template', {
                responseType: 'blob',
            }).then(res => {
                const blob = new Blob([res], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = '自动更新MSKU模板.xlsx';
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                setTimeout(() => {
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                    this.mskuBatchDownloading = false;
                }, 300);
            }).catch(() => {
                this.mskuBatchDownloading = false;
                this.$message.error('模板下载失败');
            });
        },
        mskuUploadRequest(option) {
            this.mskuBatchUploading = true;
            const fd = new FormData();
            fd.append('file', option.file);
            this.$axios.post('/vue/auto_update/msku_parse', fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            }).then(res => {
                this.mskuBatchUploading = false;
                if (res.msg === 'success' && res.data) {
                    this.mskuBatchRows = res.data.rows || [];
                    this.mskuBatchErrors = res.data.errors || [];
                    this.mskuBatchStep = 'preview';
                    if (this.mskuBatchRows.length) this.$message.success(`解析到 ${this.mskuBatchRows.length} 行`);
                } else {
                    this.$message.error((res && res.message) || '解析失败');
                }
            }).catch(e => this.$message.error('解析失败: ' + (e && e.message ? e.message : e)));
        },
        executeMskuBatch() {
            this.mskuBatchExecuting = true;
            this.$axios.post('/vue/auto_update/msku_apply', {
                rows: this.mskuBatchRows,
            }).then(res => {
                this.mskuBatchExecuting = false;
                if (res.msg === 'success' && res.data) {
                    this.mskuBatchResults = res.data.results || [];
                    this.mskuBatchSummary = res.data.summary || null;
                    this.mskuBatchStep = 'result';
                    this.refreshTableData();
                } else {
                    this.$message.error((res && res.message) || '执行失败');
                }
            }).catch(e => {
                this.mskuBatchExecuting = false;
                this.$message.error('执行失败: ' + e);
            });
        },
        // === 颜色配置（弹窗展示）===
        openColorConfig() {
            this.colorConfigVisible = true;
        },
        // === 创建并配对（选 SKU → 后端复合方法：刊登→FNSKU同步→配对，无采购）===
        openCreatePair(row) {
            this.createPairRow = { ...row };
            this.createPairSku = '';
            this.createPairSkus = [];
            this.createPairError = '';
            this.createPairVisible = true;
            this.fetchPairSkus(row['本地品名']);
        },
        fetchPairSkus(pm) {
            this.$axios.get('/vue/vue_product/pm-skus', {
                params: { pm }
            }).then(res => {
                if (res.code === 200 && res.data) {
                    this.createPairSkus = res.data || [];
                    if (!this.createPairSkus.length) {
                        this.createPairError = '该品名暂无关联 SKU，请先在备货管理中绑定';
                    }
                }
            }).catch(() => { });
        },
        submitCreatePair() {
            if (!this.createPairSku) return this.$message.warning('请选择要关联的 SKU');
            this.createPairLoading = true;
            this.$axios.post('/vue/vue_product/create-pair', {
                parent: this.createPairRow['本地父体'],
                pm: this.createPairRow['本地品名'],
                shop: this.createPairRow['店铺'],
                sku: this.createPairSku,
                suffix: 'T',
            }).then(res => {
                this.createPairLoading = false;
                if (res.code === 200 && res.data && res.data.success) {
                    this.$notify({ title: '创建并配对已提交', message: '新MSKU: ' + (res.data.new_msku || ''), duration: 5000 });
                    this.createPairVisible = false;
                    this.refreshTableData();
                } else {
                    this.createPairError = (res.data && res.data.error) || res.message || '创建配对失败';
                }
            }).catch(e => {
                this.createPairLoading = false;
                const msg = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || e;
                this.createPairError = '创建配对失败: ' + msg;
            });
        },
    }
}
</script>

<style scoped>
.msku-manage-page {
    background: #f8f9fa;
    min-height: 100vh;
    padding: 20px;
}

.msku-cell {
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
}

.msku-cell:hover .msku-edit-icon {
    color: #409EFF;
}

.msku-edit-icon {
    color: #C0C4CC;
    font-size: 12px;
}

.operation-bar {
    background: #fff;
    padding: 12px 20px;
    border-radius: 4px;
    margin-bottom: 16px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.search-form {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.search-form .form-label {
    font-size: 13px;
    color: #606266;
    white-space: nowrap;
}

.search-form .el-badge {
    line-height: 1;
}

.table-area {
    margin-top: 10px;
    background: #fff;
    padding: 20px;
    border-radius: 4px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.msku-hint {
    font-size: 12px;
    color: #909399;
    line-height: 1.6;
}

/* 批量导入配置：模板下载 / 导入配置 两个板块 */
.batch-section {
    border: 1px solid #ebeef5;
    border-radius: 4px;
    padding: 12px 16px 4px;
    margin-bottom: 16px;
}

.batch-section-title {
    font-size: 14px;
    font-weight: bold;
    color: #303133;
    margin-bottom: 12px;
}

/* 父体待填模板弹窗：父体多选表格 */
.fill-parent-table {
    border: 1px solid #ebeef5;
    border-radius: 4px;
    padding: 8px;
}

.gen-multi-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 32px;
    margin-bottom: 8px;
}

.gen-multi-empty {
    color: #E6A23C;
    font-size: 13px;
}

.gen-sku-import-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 6px;
}

/* 批量生成MSKU：行级基础名预览 */
.gen-base-ok {
    color: #409eff;
}

.gen-base-warn {
    color: #CC0000;
    background: #fef0f0;
    border-radius: 3px;
    padding: 1px 4px;
}

.gen-sku-error .el-input__inner {
    border-color: #F56C6C;
}

.gen-sku-error-message {
    color: #F56C6C;
    font-size: 11px;
    line-height: 16px;
    word-break: break-all;
}

.config-layout {
    display: flex;
    gap: 20px;
    align-items: flex-start;
}

.config-left {
    width: 340px;
    flex-shrink: 0;
    border-right: 1px solid #ebeef5;
    padding-right: 20px;
}

.config-right {
    flex: 1;
    min-width: 0;
    max-height: 560px;
    overflow-y: auto;
    padding-left: 6px;
}

.config-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 28px;
    font-size: 13px;
    color: #606266;
    background: #f5f7fa;
    border-radius: 4px;
    padding: 10px 14px;
    margin-bottom: 12px;
}

.meta-item {
    display: inline-flex;
    align-items: center;
}

.meta-label {
    color: #909399;
    margin-right: 6px;
}

.config-empty {
    color: #909399;
    font-size: 13px;
    padding: 12px 0;
}

.dims-row {
    display: flex;
    align-items: center;
    gap: 6px;
}

.dims-x {
    color: #909399;
}

.dims-unit {
    margin-left: 2px;
    font-size: 12px;
    color: #909399;
    white-space: nowrap;
}

::v-deep .warn-row {
    background-color: #fef0f0 !important;
}
</style>

<!-- 全局样式：弹窗 append-to-body 渲染在 body 下，scoped 深度选择器无法命中，
     且 Element 用行内 margin-top（top 属性默认 15vh），必须 !important 覆盖。
     调整下方数值即可整体上移/下移弹窗。 -->
<style>
.au-dialog {
    margin-top: max(0px, calc(15vh - 150px)) !important;
}

/* 主流程四个弹窗在原位置基础上下移 50px，其它旧弹窗保持原位置。 */
.main-feature-dialog {
    margin-top: max(0px, calc(15vh - 100px)) !important;
}

/* 固定弹窗高度：内容超出时在 body 区内部滚动，避免把弹窗撑高/溢出视口 */
.au-dialog {
    height: min(680px, 85vh) !important;
    display: flex;
    flex-direction: column;
}

.au-dialog .el-dialog__body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
}
</style>
